import { useState } from 'react';
import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import type { Client } from '@packages/types';
import { listClients } from '@packages/api/endpoints/client.js';
import { useDebouncedValue } from './useDebouncedValue';
import { queryKeys, getNextPageParam, flattenPages, DEFAULT_LIST_PAGE_SIZE } from '@packages/api';

interface ComboboxOption {
  value: string;
  label: string;
}

/** The picker's read for one search term; `''` is what an untouched picker shows. */
export function clientSearchQueryOptions(accountId: string, search: string, limit?: number) {
  return infiniteQueryOptions({
    queryKey: [...queryKeys.clients(accountId), 'search', search, limit],
    queryFn: ({ pageParam }) =>
      listClients(accountId, {
        search: search || undefined,
        cursor: pageParam,
        limit: limit ?? DEFAULT_LIST_PAGE_SIZE,
      }),
    getNextPageParam,
    initialPageParam: undefined as string | undefined,
  });
}

/**
 * Reusable hook for server-side searchable client pickers.
 * Returns options for <Combobox onSearch> and the full Client[] for rate lookups.
 */
export function useClientSearch(
  accountId: string,
  options?: { enabled?: boolean; limit?: number },
) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);

  const limit = options?.limit;

  const {
    data,
    hasNextPage,
    fetchNextPage,
    isLoading,
    isError,
    isFetchingNextPage,
  } = useInfiniteQuery({
    ...clientSearchQueryOptions(accountId, debouncedSearch, limit),
    enabled: (options?.enabled ?? true) && !!accountId,
  });

  const clients: Client[] = flattenPages(data?.pages ?? []);
  const clientOptions: ComboboxOption[] = clients.map((c) => ({
    value: c.id,
    label: c.name,
  }));

  return {
    clients,
    clientOptions,
    search,
    setSearch,
    /** True while the typed search has not yet reached the query (the 300ms debounce). */
    isSearchPending: search !== debouncedSearch,
    hasNextPage,
    fetchNextPage,
    isLoading,
    isError,
    isFetchingNextPage,
  };
}
